import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import ReactDOM from 'react-dom';
import AntLocalization from '../../../utils/AntLocalization';
import AntAPI from '../../../utils/AntAPI';
import { AntSession } from '../../../utils/AntContext';
import FloatingWorkPanel from '../../FloatingWorkPanel';
import AntFileViewer from '../../AntFileViewer';
import { getInitial } from '../shared';
import { SidebarState, sidebarRelativeTime, sidebarDate, sidebarTime } from './SidebarStandard';
import './ChatPanel.scss';

// Internal chat for the web: one component for the right-sidebar tab (mode 'sidebar'),
// the expanded floating panel ('floating') and the full page ('embed').
// Same features and rules as ante-mobile-user src/screens/chat.

var T = function (text) { return AntLocalization.translateText(text); };
var PAGE_SIZE = 30;
var chatAPI = new AntAPI('user', 'ACChat');
var msgAPI = new AntAPI('user', 'ACChatMessage');
var readAPI = new AntAPI('user', 'ACChatRead');
var userAPI = new AntAPI('user', 'ACUserAccount');

// ─── Data helpers ─────────────────────────────────────────────
function call(api, action, body) {
    return api.postAsync('/' + api.routeURL + '/' + action, body || {}).then(function (result) {
        var data = result && result.data !== undefined ? result.data : result;
        if (!result || result.error || (data && (data.error || data.success === false))) throw new Error('Request failed');
        return data;
    });
}

function upload(api, action, formData) {
    return new Promise(function (resolve, reject) {
        api.upload('/' + api.routeURL + '/' + action, formData, function (result) {
            var data = result && result.data !== undefined ? result.data : result;
            if (!result || result.error || (data && data.error)) reject(new Error('Upload failed'));
            else resolve(data);
        });
    });
}

function rowsOf(data) {
    var rows = (data && data.rows) || (data instanceof Array ? data : []);
    return rows instanceof Array ? rows : [];
}

function membersOf(chat) {
    if (!chat) return [];
    if (chat.member_profiles instanceof Array) return chat.member_profiles;
    return (chat.member_avatars || []).map(function (m) { return { code: m.code, display_name: m.entity_name, avatar_full_path: m.avatar }; });
}

// Same rule as ACChat.leaderOf on the server.
function leaderOf(chat) {
    if (!chat) return null;
    if (chat.leader_user_code) return chat.leader_user_code;
    var members = membersOf(chat).map(function (m) { return m.code; });
    var admins = chat.admin_user_codes instanceof Array ? chat.admin_user_codes : [];
    for (var i = 0; i < admins.length; i++) if (members.indexOf(admins[i]) >= 0) return admins[i];
    return members.indexOf(chat.created_user_code) >= 0 ? chat.created_user_code : members[0] || null;
}

function titleOf(chat, me) {
    if (!chat) return '';
    if (chat.name) return chat.name;
    var others = membersOf(chat).filter(function (m) { return m.code !== me; }).map(function (m) { return m.display_name || m.code; });
    return others.join(', ') || T({ vi: 'Trao đổi', en: 'Chat', fr: 'Chat', es: 'Chat', id: 'Chat', ja: 'チャット', zh: '聊天', ko: '채팅', hi: 'चैट', ar: 'محادثة' });
}

function toMillis(value) {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'string' && !/^\d+$/.test(value)) return Date.parse(value) || 0;
    var n = Number(value);
    return n < 1e12 ? n * 1000 : n;
}

function dayLabel(value) {
    var ms = toMillis(value);
    if (!ms) return '';
    var d = new Date(ms), today = new Date();
    var start = function (x) { return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime(); };
    var diff = Math.round((start(today) - start(d)) / 86400000);
    if (diff === 0) return T({ vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' });
    if (diff === 1) return T({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल', ar: 'أمس' });
    return sidebarDate(ms, false);
}

function fileOf(message) {
    return { id: message.meddoc_id, name: message.meddoc_name || message.content, url: message.file_full_path || message.file_short_path, type: message.file_type, size: message.file_size };
}

var IMAGE_RE = /\.(jpe?g|png|gif|webp|bmp|svg|heic)$/i;
function isImage(file) {
    return file.type === 'Image' || IMAGE_RE.test(file.name || '');
}

function fileSize(bytes) {
    var n = Number(bytes);
    if (!n) return '';
    if (n < 1024) return n + ' B';
    if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
    return (n / 1048576).toFixed(1) + ' MB';
}

function fileExt(name) {
    var m = /\.([a-z0-9]{1,5})$/i.exec(name || '');
    return m ? m[1].toUpperCase() : T({ vi: 'TỆP', en: 'FILE', fr: 'FICHIER', es: 'ARCHIVO', id: 'BERKAS', ja: 'FILE', zh: '文件', ko: '파일', hi: 'फ़ाइल', ar: 'ملف' });
}

function likedBy(message) {
    var codes = message.liked_user_codes;
    if (codes instanceof Array) return codes;
    if (typeof codes === 'string' && codes) {
        try { var parsed = JSON.parse(codes); if (parsed instanceof Array) return parsed; } catch (e) { /* comma list */ }
        return codes.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    return [];
}

var URL_RE = /(https?:\/\/[^\s<>"']+)/g;
function linkify(text) {
    if (!text) return null;
    return String(text).split(URL_RE).map(function (part, i) {
        if (i % 2 === 1) {
            var url = part.replace(/[.,;:!?)\]]+$/, '');
            return (
                <Fragment key={i}>
                    <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                    {part.slice(url.length)}
                </Fragment>
            );
        }
        return <Fragment key={i}>{part}</Fragment>;
    });
}

function currentUserFromStorage() {
    try {
        var keys = Object.keys(localStorage);
        for (var i = 0; i < keys.length; i++) {
            if (keys[i].endsWith('-session-info')) {
                var info = JSON.parse(localStorage.getItem(keys[i]));
                if (info && info.user_code) return info.user_code;
            }
        }
    } catch (e) { /* no session info */ }
    return null;
}

// ─── Icons ────────────────────────────────────────────────────
var ICONS = {
    back: 'M15 19l-7-7 7-7',
    close: 'M6 18L18 6M6 6l12 12',
    search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
    plus: 'M12 5v14M5 12h14',
    info: 'M12 16v-4m0-4h.01M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z',
    pin: 'M9 4h6l-1 6 4 4H6l4-4-1-6zM12 14v7',
    eyeOff: 'M3 3l18 18M10.58 10.58a2 2 0 002.83 2.83M9.88 5.08A9.77 9.77 0 0112 5c5 0 9 4.5 10 7a13.3 13.3 0 01-2.62 3.66M6.1 6.1C3.9 7.6 2.5 9.8 2 12c1 2.5 5 7 10 7a9.9 9.9 0 004.24-.92',
    eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 100-6 3 3 0 000 6z',
    users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
    media: 'M4 16l4.59-4.59a2 2 0 012.82 0L16 16m-2-2l1.59-1.59a2 2 0 012.82 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
    sweep: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
    reply: 'M9 14L4 9l5-5M4 9h10a6 6 0 016 6v3',
    heart: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z',
    more: 'M5 12h.01M12 12h.01M19 12h.01',
    copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
    trash: 'M19 7l-.87 12.14A2 2 0 0116.14 21H7.86a2 2 0 01-2-1.86L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
    attach: 'M15.17 7l-6.59 6.59a2 2 0 102.83 2.83l6.41-6.59a4 4 0 00-5.66-5.66l-6.41 6.59a6 6 0 108.49 8.49L20.5 13',
    smile: 'M12 22a10 10 0 100-20 10 10 0 000 20zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01',
    send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
    edit: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.41-9.41a2 2 0 112.83 2.83L11.83 15H9v-2.83l8.59-8.58z',
    logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
    userPlus: 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 11a4 4 0 100-8 4 4 0 000 8zM20 8v6M23 11h-6',
    check: 'M20 6L9 17l-5-5',
    link: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
    file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6',
    chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.42-4.03 8-9 8a9.86 9.86 0 01-4.26-.95L3 20l1.4-3.72C3.51 15.04 3 13.57 3 12c0-4.42 4.03-8 9-8s9 3.58 9 8z',
    expand: 'M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4'
};

function Icon(props) {
    return (
        <svg width={props.size || 18} height={props.size || 18} viewBox="0 0 24 24" fill={props.fill || 'none'} stroke="currentColor"
            strokeWidth={props.stroke || 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d={ICONS[props.name]} />
        </svg>
    );
}

function IconButton(props) {
    return (
        <button type="button" className={'cw-icon-btn' + (props.className ? ' ' + props.className : '') + (props.active ? ' active' : '')}
            onClick={props.onClick} disabled={props.disabled} title={T(props.label)} aria-label={T(props.label)}>
            <Icon name={props.icon} size={props.size} />
        </button>
    );
}

function Avatar(props) {
    var size = props.size || 36;
    var style = { width: size, height: size, fontSize: Math.round(size * 0.4) };
    if (props.src) return <img className="cw-avatar" style={style} src={props.src} alt="" />;
    return <span className="cw-avatar" style={style}>{getInitial(props.name)}</span>;
}

function ChatAvatar(props) {
    var chat = props.chat, me = props.me, size = props.size || 44;
    var others = membersOf(chat).filter(function (m) { return m.code !== me; });
    if (chat.type !== 'direct' && others.length > 1) {
        var a = others[0], b = others[1];
        var small = Math.round(size * 0.66);
        return (
            <span className="cw-avatar-group" style={{ width: size, height: size }}>
                <span className="cw-avatar-group-back"><Avatar src={a.avatar_full_path} name={a.display_name || a.code} size={small} /></span>
                <span className="cw-avatar-group-front"><Avatar src={b.avatar_full_path} name={b.display_name || b.code} size={small} /></span>
            </span>
        );
    }
    var other = others[0] || {};
    return <Avatar src={other.avatar_full_path} name={other.display_name || titleOf(chat, me)} size={size} />;
}

// ─── Small overlays ───────────────────────────────────────────
function ConfirmDialog(props) {
    if (!props.state) return null;
    var s = props.state;
    return ReactDOM.createPortal(
        <div className="cw-overlay" onMouseDown={function (e) { if (e.target === e.currentTarget) props.onCancel(); }}>
            <div className="cw-dialog" role="alertdialog" aria-modal="true">
                <div className="cw-dialog-title">{T(s.title)}</div>
                {s.message && <div className="cw-dialog-text">{T(s.message)}</div>}
                <div className="cw-dialog-actions">
                    <button type="button" className="cw-btn cw-btn--ghost" onClick={props.onCancel}>{T({ vi: 'Huỷ', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء', ja: '取消' })}</button>
                    <button type="button" className={'cw-btn ' + (s.danger ? 'cw-btn--danger' : 'cw-btn--primary')} onClick={props.onConfirm}>{T(s.okText)}</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

// Popover menu anchored at a point (message context menu) or under a button (chat info menu).
function PopMenu(props) {
    var ref = useRef(null);
    var [pos, setPos] = useState({ left: props.x, top: props.y, ready: false });
    useEffect(function () {
        if (!ref.current) return;
        var r = ref.current.getBoundingClientRect();
        var left = props.alignRight ? props.x - r.width : props.x;
        var top = props.y;
        left = Math.max(8, Math.min(left, window.innerWidth - r.width - 8));
        if (top + r.height > window.innerHeight - 8) top = Math.max(8, props.y - r.height - (props.flipOffset || 0));
        setPos({ left: left, top: top, ready: true });
    }, [props.x, props.y, props.alignRight, props.flipOffset]);
    useEffect(function () {
        function onDown(e) { if (ref.current && !ref.current.contains(e.target)) props.onClose(); }
        function onKey(e) { if (e.key === 'Escape') props.onClose(); }
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', props.onClose);
        return function () {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', props.onClose);
        };
    }, [props.onClose]);
    return ReactDOM.createPortal(
        <div ref={ref} className="cw-menu" role="menu" style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}>
            {props.header && <div className="cw-menu-header">{props.header}</div>}
            {props.items.filter(Boolean).map(function (item, i) {
                if (item.divider) return <div key={i} className="cw-menu-divider" />;
                return (
                    <button key={i} type="button" role="menuitem" className={'cw-menu-item' + (item.danger ? ' danger' : '')}
                        onClick={function () { props.onClose(); item.onClick(); }}>
                        <Icon name={item.icon} size={17} />
                        <span>{T(item.label)}</span>
                    </button>
                );
            })}
        </div>,
        document.body
    );
}

var EMOJIS = ['😀', '😄', '😂', '🤣', '🙂', '😊', '😍', '🥰', '😘', '😎', '🤩', '🥳', '😏', '😅', '😢', '😭', '😤', '😡', '🤔', '😴',
    '👍', '👎', '👏', '🙌', '🙏', '💪', '👌', '✌️', '🤝', '👋', '❤️', '💙', '💚', '💯', '🔥', '⭐', '✨', '🎉', '✅', '❌'];
var EMOJI_SHORTCUTS = [[':-)', '😊'], [':)', '🙂'], [':(', '😞'], [';)', '😉'], [':D', '😄'], [':P', '😛'], ['<3', '❤️'], [':*', '😘'], [':thumbsup:', '👍'], [':fire:', '🔥']];
function convertEmoji(text) {
    var out = text;
    EMOJI_SHORTCUTS.forEach(function (pair) { out = out.split(pair[0]).join(pair[1]); });
    return out;
}

// ─── Main ─────────────────────────────────────────────────────
export default function ChatPanel(props) {
    var mode = props.mode || 'sidebar';
    var session = AntSession ? AntSession() : null;
    var me = useMemo(function () { return (session && session.user_code) || currentUserFromStorage(); }, [session]);
    var onMarkRead = props.onMarkRead, onUpdateUnread = props.onUpdateUnread, activeChatIdRef = props.activeChatIdRef;

    // List
    var [chats, setChats] = useState([]);
    var [listState, setListState] = useState('loading'); // loading | ready | error
    var [listMore, setListMore] = useState(false);
    var [search, setSearch] = useState('');
    var [readIds, setReadIds] = useState({});
    var listPage = useRef(0), listVersion = useRef(0);
    // Views
    var [view, setView] = useState('list'); // list | create (left column)
    var [active, setActive] = useState(null);
    var [sub, setSub] = useState('messages'); // messages | members | add | media
    // Messages
    var [messages, setMessages] = useState([]);
    var [msgState, setMsgState] = useState('idle');
    var [msgMore, setMsgMore] = useState(false);
    var [loadingOlder, setLoadingOlder] = useState(false);
    var [text, setText] = useState('');
    var [sending, setSending] = useState(false);
    var [files, setFiles] = useState([]);
    var [reply, setReply] = useState(null);
    var [emojiOpen, setEmojiOpen] = useState(false);
    var [dragOver, setDragOver] = useState(false);
    var [typing, setTyping] = useState([]);
    // Overlays
    var [infoMenu, setInfoMenu] = useState(null);
    var [msgMenu, setMsgMenu] = useState(null);
    var [confirm, setConfirm] = useState(null);
    var [viewer, setViewer] = useState(null);
    var [notice, setNotice] = useState('');

    var activeRef = useRef(null), msgVersion = useRef(0), scrollRef = useRef(null), atBottom = useRef(true);
    var messagesRef = useRef([]);
    messagesRef.current = messages;
    var inputRef = useRef(null), fileRef = useRef(null), typingTimers = useRef({}), typingSent = useRef(0), noticeTimer = useRef(null);
    activeRef.current = active;

    var flash = useCallback(function (message) {
        setNotice(T(message || { vi: 'Chưa thực hiện được, vui lòng thử lại', en: 'Something went wrong, please try again', fr: 'Veuillez réessayer', es: 'Inténtelo de nuevo', id: 'Silakan coba lagi', ja: 'もう一度お試しください', zh: '请重试', ko: '다시 시도해 주세요', hi: 'कृपया पुनः प्रयास करें', ar: 'يرجى المحاولة مجددًا' }));
        clearTimeout(noticeTimer.current);
        noticeTimer.current = setTimeout(function () { setNotice(''); }, 3500);
    }, []);

    // ─── Chat list ────────────────────────────────────────────
    var loadChats = useCallback(function (page) {
        var current = ++listVersion.current;
        var next = page || 0;
        if (!next) setListState(function (s) { return s === 'ready' ? s : 'loading'; });
        return call(chatAPI, 'list', { pageSize: PAGE_SIZE, pageIndex: next, searchText: search.trim() || undefined }).then(function (data) {
            if (current !== listVersion.current) return;
            var rows = rowsOf(data);
            setChats(function (prev) {
                if (!next) return rows;
                var seen = {};
                prev.forEach(function (c) { seen[String(c.id)] = true; });
                return prev.concat(rows.filter(function (c) { return !seen[String(c.id)]; }));
            });
            listPage.current = next;
            setListMore(rows.length >= PAGE_SIZE);
            setListState('ready');
            if (!next && onUpdateUnread && !search.trim() && rows.some(function (r) { return r.has_unread !== undefined; })) {
                var unread = {};
                rows.forEach(function (r) { if (r.has_unread) unread[String(r.id)] = true; });
                onUpdateUnread(unread, rows.map(function (r) { return String(r.id); }));
            }
            setActive(function (a) {
                if (!a) return a;
                var fresh = rows.find(function (r) { return String(r.id) === String(a.id); });
                return fresh ? Object.assign({}, a, fresh) : a;
            });
        }).catch(function () { if (current === listVersion.current) setListState('error'); });
    }, [search, onUpdateUnread]);

    var visible = mode === 'floating' ? props.visible !== false : true;
    useEffect(function () {
        if (!visible) return undefined;
        var timer = setTimeout(function () { loadChats(0); }, search ? 300 : 0);
        return function () { clearTimeout(timer); };
    }, [visible, loadChats, search]);

    // ─── Messages ─────────────────────────────────────────────
    var scrollToBottom = useCallback(function () {
        requestAnimationFrame(function () {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            atBottom.current = true;
        });
    }, []);

    var markRead = useCallback(function (chat) {
        if (!chat) return;
        call(readAPI, 'markRead', { chat_id: chat.id, chat_code: chat.code }).catch(function () { /* next open retries */ });
        setReadIds(function (prev) { var n = Object.assign({}, prev); n[String(chat.id)] = true; return n; });
        if (onMarkRead) onMarkRead(chat.id);
    }, [onMarkRead]);

    var loadMessages = useCallback(function (chat, older) {
        if (!chat) return Promise.resolve();
        var current = ++msgVersion.current;
        var oldest = older ? messagesRef.current[0] : null;
        if (older) setLoadingOlder(true); else setMsgState(function (s) { return messagesRef.current.length ? s : 'loading'; });
        var el = scrollRef.current, prevHeight = el ? el.scrollHeight : 0;
        return call(msgAPI, 'list', Object.assign({ chat_id: chat.id, chat_code: chat.code, pageSize: PAGE_SIZE, pageIndex: 0 },
            oldest ? { before_id: oldest.id, before_at: Math.floor(toMillis(oldest.created_at) / 1000) } : {})).then(function (data) {
            if (current !== msgVersion.current || !activeRef.current || String(activeRef.current.id) !== String(chat.id)) return;
            var rows = rowsOf(data).slice().reverse();
            setMessages(function (prev) {
                if (older) return rows.concat(prev);
                var ids = {};
                rows.forEach(function (r) { ids[String(r.id)] = true; });
                var cutoff = rows.length ? toMillis(rows[0].created_at) : 0;
                return prev.filter(function (p) { return !ids[String(p.id)] && toMillis(p.created_at) < cutoff; }).concat(rows);
            });
            if (older || !messagesRef.current.length) setMsgMore(rows.length >= PAGE_SIZE);
            setMsgState('ready');
            if (older) requestAnimationFrame(function () { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
            else if (atBottom.current) scrollToBottom();
        }).catch(function () { if (current === msgVersion.current) setMsgState('error'); })
            .finally(function () { if (older) setLoadingOlder(false); });
    }, [scrollToBottom]);

    var openChat = useCallback(function (chat) {
        if (!chat) return;
        setActive(chat);
        setSub('messages');
        setMessages([]);
        messagesRef.current = [];
        setMsgMore(false);
        setMsgState('loading');
        setText(''); setFiles([]); setReply(null); setEmojiOpen(false); setTyping([]);
        atBottom.current = true;
        activeRef.current = chat;
        loadMessages(chat).then(function () { markRead(chat); });
        setTimeout(function () { if (inputRef.current) inputRef.current.focus(); }, 120);
    }, [loadMessages, markRead]);

    var closeChat = useCallback(function () {
        setActive(null);
        activeRef.current = null;
        setSub('messages');
        setMessages([]);
    }, []);

    useEffect(function () {
        if (activeChatIdRef) activeChatIdRef.current = active && sub === 'messages' ? String(active.id) : null;
    }, [active, sub, activeChatIdRef]);

    // Open a requested chat (toast click, query string) once the list has it.
    var requestedId = props.pendingOpenChatId || props.initialChatId;
    var requestedCode = props.initialChatCode;
    var handledRequest = useRef(null);
    useEffect(function () {
        var key = String(requestedId || requestedCode || '');
        if (!key || handledRequest.current === key || !chats.length) return;
        var match = chats.find(function (c) { return String(c.id) === String(requestedId) || (requestedCode && c.code === requestedCode); });
        if (!match) return;
        handledRequest.current = key;
        openChat(match);
        if (props.onClearPendingChat) props.onClearPendingChat();
    }, [requestedId, requestedCode, chats, openChat, props.onClearPendingChat]);

    // ─── Socket ───────────────────────────────────────────────
    useEffect(function () {
        if (!session || !session.registerSocketListener) return undefined;
        var key = 'AntChatPanel-' + mode;
        session.registerSocketListener(function (data) {
            if (!data) return false;
            var chat = activeRef.current, isActive = chat && String(data.chat_id) === String(chat.id);
            if (data.event === 'NewChatMessage') {
                loadChats(0);
                if (isActive) {
                    loadMessages(chat);
                    if (!document.hidden) markRead(chat);
                } else {
                    setReadIds(function (prev) { if (!prev[String(data.chat_id)]) return prev; var n = Object.assign({}, prev); delete n[String(data.chat_id)]; return n; });
                }
                return false;
            }
            if (data.event === 'ChatRead' || data.event === 'ChatUpdated' || data.event === 'ChatDeleted') {
                loadChats(0);
                if (isActive && data.event === 'ChatDeleted') closeChat();
                else if (isActive && data.event === 'ChatUpdated') {
                    call(chatAPI, 'list', { code: chat.code, pageSize: 1 }).then(function (d) {
                        var fresh = rowsOf(d).find(function (r) { return r.code === chat.code; });
                        if (fresh) { setActive(fresh); loadMessages(fresh); } else closeChat();
                    }).catch(function () { /* list refresh already scheduled */ });
                }
                return false;
            }
            if (data.event === 'ChatTyping' && isActive && data.user_code !== me) {
                var code = data.user_code;
                setTyping(function (prev) { return prev.some(function (u) { return u.code === code; }) ? prev : prev.concat([{ code: code, name: data.user_name }]); });
                clearTimeout(typingTimers.current[code]);
                typingTimers.current[code] = setTimeout(function () {
                    setTyping(function (prev) { return prev.filter(function (u) { return u.code !== code; }); });
                }, 3000);
                return true;
            }
            return false;
        }, key);
        return function () { session.clearSocketListener(key); };
    }, [session, mode, me, loadChats, loadMessages, markRead, closeChat]);

    // ─── Sending ──────────────────────────────────────────────
    var send = useCallback(function () {
        var chat = activeRef.current;
        var content = convertEmoji(text.trim());
        if (!chat || sending || (!content && !files.length)) return;
        setSending(true);
        var replyId = reply ? reply.id : undefined;
        var queue = Promise.resolve();
        if (content) queue = queue.then(function () { return call(msgAPI, 'create', { chat_id: chat.id, chat_code: chat.code, content_type: 'text', content: content, reply_message_id: replyId }); });
        files.forEach(function (file, i) {
            queue = queue.then(function () {
                var fd = new FormData();
                fd.append('files', file);
                fd.append('chat_id', chat.id);
                fd.append('chat_code', chat.code || '');
                if (!content && i === 0 && replyId) fd.append('reply_message_id', replyId);
                return upload(msgAPI, 'createWithAttachment', fd);
            });
        });
        queue.then(function () {
            setText(''); setFiles([]); setReply(null); setEmojiOpen(false);
            atBottom.current = true;
            return loadMessages(chat);
        }).then(function () { markRead(chat); loadChats(0); })
            .catch(function () { flash({ vi: 'Chưa gửi được tin nhắn, vui lòng thử lại', en: 'Message not sent, please try again', fr: 'Message non envoyé, veuillez réessayer', es: 'Mensaje no enviado, inténtelo de nuevo', id: 'Pesan belum terkirim, silakan coba lagi', ja: 'メッセージを送信できません。再試行してください', zh: '消息未发送，请重试', ko: '메시지를 보내지 못했습니다. 다시 시도하세요', hi: 'संदेश नहीं भेजा गया, पुनः प्रयास करें', ar: 'تعذر إرسال الرسالة، حاول مرة أخرى' }); })
            .finally(function () {
                setSending(false);
                if (inputRef.current) inputRef.current.focus();
            });
    }, [text, files, reply, sending, loadMessages, loadChats, markRead, flash]);

    var onType = useCallback(function (e) {
        setText(e.target.value);
        var el = e.target;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 140) + 'px';
        var chat = activeRef.current;
        if (chat && Date.now() - typingSent.current > 2500) {
            typingSent.current = Date.now();
            call(msgAPI, 'actionTyping', { chat_id: chat.id }).catch(function () { /* typing is best effort */ });
        }
    }, []);

    var onKeyDown = useCallback(function (e) {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
        if (e.key === 'Escape' && reply) setReply(null);
    }, [send, reply]);

    useEffect(function () {
        if (!text && inputRef.current) inputRef.current.style.height = '';
    }, [text]);

    // ─── Message actions ──────────────────────────────────────
    var toggleLike = useCallback(function (m) {
        var liked = likedBy(m).indexOf(me) >= 0;
        setMessages(function (prev) {
            return prev.map(function (row) {
                if (row.id !== m.id) return row;
                var codes = likedBy(row).filter(function (c) { return c !== me; });
                if (!liked) codes.push(me);
                return Object.assign({}, row, { liked_user_codes: codes, liked_count: codes.length });
            });
        });
        call(msgAPI, liked ? 'unlikeMessage' : 'likeMessage', { id: m.id }).catch(function () {
            flash();
            loadMessages(activeRef.current);
        });
    }, [me, flash, loadMessages]);

    var startReply = useCallback(function (m) {
        setReply(m);
        if (inputRef.current) inputRef.current.focus();
    }, []);

    var copyMessage = useCallback(function (m) {
        var done = function () { flash({ vi: 'Đã sao chép', en: 'Copied', fr: 'Copié', es: 'Copiado', id: 'Disalin', ja: 'コピー済み', zh: '已复制', ko: '복사됨', hi: 'कॉपी हो गया', ar: 'تم النسخ' }); };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(m.content || '').then(done).catch(function () { flash(); });
    }, [flash]);

    var deleteMessage = useCallback(function (m) {
        setConfirm({
            title: { vi: 'Xoá tin nhắn', en: 'Delete message', fr: 'Supprimer message', es: 'Eliminar mensaje', id: 'Hapus pesan', ja: 'メッセージ削除', zh: '删除消息', ko: '메시지 삭제', hi: 'संदेश हटाएँ', ar: 'حذف الرسالة' },
            message: { vi: 'Tin nhắn sẽ bị xoá với mọi người trong cuộc trao đổi.', en: 'The message is deleted for everyone in this chat.', fr: 'Le message sera supprimé pour tous dans ce chat.', es: 'El mensaje se eliminará para todos en este chat.', id: 'Pesan akan dihapus untuk semua orang di chat ini.', ja: 'メッセージはこのチャットの全員から削除されます。', zh: '该消息将对聊天中的所有人删除。', ko: '이 채팅의 모든 사람에게서 메시지가 삭제됩니다.', hi: 'यह संदेश चैट में सभी के लिए हट जाएगा।', ar: 'ستُحذف الرسالة لدى الجميع في هذه المحادثة.' },
            okText: { vi: 'Xoá', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' }, danger: true,
            onOk: function () {
                call(msgAPI, 'deleteMessage', { id: m.id }).then(function () {
                    setMessages(function (prev) {
                        return prev.map(function (row) {
                            return row.id === m.id ? { id: row.id, user_code: row.user_code, user_name: row.user_name, user_profile: row.user_profile, avatar_full_path: row.avatar_full_path, created_at: row.created_at, is_deleted: true } : row;
                        });
                    });
                    loadChats(0);
                }).catch(function () { flash(); });
            }
        });
    }, [flash, loadChats]);

    var openMessageMenu = useCallback(function (e, m) {
        e.preventDefault();
        var rect = e.currentTarget.getBoundingClientRect ? e.currentTarget.getBoundingClientRect() : null;
        var fromButton = e.type === 'click' && rect;
        setMsgMenu({ message: m, x: fromButton ? rect.right : e.clientX, y: fromButton ? rect.bottom + 4 : e.clientY, alignRight: !!fromButton, flipOffset: fromButton ? rect.height + 8 : 0 });
    }, []);

    // ─── Chat actions (info menu) ─────────────────────────────
    var setPreference = useCallback(function (change) {
        var chat = activeRef.current;
        if (!chat) return;
        call(readAPI, 'preference', Object.assign({ chat_id: chat.id }, change)).then(function (res) {
            var pinned = res && res.pinned !== undefined ? res.pinned === 1 : chat.my_pinned;
            var hidden = res && res.hidden !== undefined ? res.hidden === 1 : chat.my_hidden;
            setActive(Object.assign({}, chat, { my_pinned: pinned, my_hidden: hidden }));
            flash(change.pinned !== undefined
                ? (change.pinned ? { vi: 'Đã ghim trò chuyện', en: 'Chat pinned', fr: 'Chat épinglé', es: 'Chat fijado', id: 'Chat disematkan', ja: 'ピン留めしました', zh: '已置顶聊天', ko: '채팅 고정됨', hi: 'चैट पिन की गई', ar: 'تم تثبيت المحادثة' } : { vi: 'Đã bỏ ghim', en: 'Chat unpinned', fr: 'Chat détaché', es: 'Chat desfijado', id: 'Pin dilepas', ja: 'ピン解除済み', zh: '已取消置顶', ko: '고정 해제됨', hi: 'चैट अनपिन हुई', ar: 'تم فك التثبيت' })
                : (change.hidden ? { vi: 'Đã ẩn trò chuyện. Tìm kiếm để mở lại.', en: 'Chat hidden. Search to find it again.', fr: 'Chat masqué. Recherchez-le pour le rouvrir.', es: 'Chat oculto. Búsquelo para reabrirlo.', id: 'Chat disembunyikan. Cari untuk membukanya.', ja: '非表示にしました。検索で開けます。', zh: '聊天已隐藏。搜索可重新打开。', ko: '채팅을 숨겼습니다. 검색하면 다시 열립니다.', hi: 'चैट छिपाई गई। खोजकर फिर खोलें।', ar: 'تم إخفاء المحادثة. ابحث لفتحها مجدداً.' } : { vi: 'Đã bỏ ẩn', en: 'Chat unhidden', fr: 'Chat affiché', es: 'Chat visible', id: 'Chat ditampilkan', ja: 'チャット再表示', zh: '已取消隐藏', ko: '숨김 해제됨', hi: 'चैट दिखाई गई', ar: 'تم الإظهار' }));
            loadChats(0);
        }).catch(function () { flash(); });
    }, [flash, loadChats]);

    var clearHistory = useCallback(function () {
        setConfirm({
            title: { vi: 'Xoá lịch sử', en: 'Clear history', fr: 'Vider historique', es: 'Borrar historial', id: 'Hapus riwayat', ja: '履歴を消去', zh: '清除记录', ko: '기록 지우기', hi: 'इतिहास मिटाएँ', ar: 'مسح السجل' },
            message: { vi: 'Tin nhắn cũ sẽ bị xoá ở phía bạn. Người khác vẫn xem được, cuộc trao đổi vẫn giữ nguyên.', en: 'Earlier messages are removed for you only. Others keep them and the chat stays.', fr: 'Les anciens messages sont supprimés pour vous seul. Les autres les gardent, le chat reste.', es: 'Los mensajes anteriores se borran solo para ti. Los demás los conservan y el chat sigue.', id: 'Pesan lama dihapus hanya untuk Anda. Orang lain tetap melihatnya, chat tetap ada.', ja: '過去のメッセージはあなた側のみ削除されます。他の人には残り、チャットも維持されます。', zh: '旧消息仅在您这边删除。其他人仍可查看，聊天保留。', ko: '이전 메시지는 내 쪽에서만 삭제됩니다. 다른 사람은 계속 볼 수 있고 채팅은 유지됩니다.', hi: 'पुराने संदेश केवल आपके लिए हटेंगे। अन्य लोग उन्हें देख सकेंगे, चैट बनी रहेगी।', ar: 'تُحذف الرسائل القديمة لديك فقط. يحتفظ بها الآخرون وتبقى المحادثة.' },
            okText: { vi: 'Xoá lịch sử', en: 'Clear', fr: 'Effacer', es: 'Borrar', id: 'Hapus', ja: '消去', zh: '清除', ko: '지우기', hi: 'मिटाएँ', ar: 'مسح' }, danger: true,
            onOk: function () {
                var chat = activeRef.current;
                call(readAPI, 'clearHistory', { chat_id: chat.id }).then(function () {
                    setMessages([]); setMsgMore(false);
                    loadChats(0);
                }).catch(function () { flash(); });
            }
        });
    }, [flash, loadChats]);

    var runChatAction = useCallback(function (action, body, after) {
        return call(chatAPI, action, body).then(after).catch(function () { flash(); });
    }, [flash]);

    var leaveGroup = useCallback(function () {
        var chat = activeRef.current, isLeader = leaderOf(chat) === me;
        setConfirm({
            title: { vi: 'Rời nhóm', en: 'Leave group', fr: 'Quitter groupe', es: 'Dejar el grupo', id: 'Keluar grup', ja: 'グループ退出', zh: '退出群组', ko: '그룹 나가기', hi: 'समूह छोड़ें', ar: 'غادر المجموعة' },
            message: isLeader
                ? { vi: 'Bạn là trưởng nhóm. Vai trò này sẽ chuyển cho thành viên trao đổi lâu nhất.', en: 'You lead this group. The role passes to the longest-standing participant.', fr: 'Vous dirigez ce groupe. Le rôle passera au membre le plus ancien.', es: 'Lideras este grupo. El rol pasará al miembro más antiguo.', id: 'Anda ketua grup. Peran ini beralih ke anggota terlama.', ja: 'あなたはリーダーです。役割は最古参のメンバーに移ります。', zh: '您是群主。该角色将转给最早加入的成员。', ko: '회원님은 그룹장입니다. 역할은 가장 오래된 멤버에게 넘어갑니다.', hi: 'आप समूह प्रमुख हैं। यह भूमिका सबसे पुराने सदस्य को मिलेगी।', ar: 'أنت قائد المجموعة. سينتقل الدور إلى أقدم عضو.' }
                : { vi: 'Bạn sẽ không nhận tin nhắn của nhóm nữa.', en: 'You will stop receiving messages from this group.', fr: 'Vous ne recevrez plus les messages du groupe.', es: 'Dejarás de recibir mensajes del grupo.', id: 'Anda tidak lagi menerima pesan grup.', ja: 'このグループのメッセージは届かなくなります。', zh: '您将不再收到该群的消息。', ko: '이 그룹의 메시지를 더 이상 받지 않습니다.', hi: 'आपको समूह के संदेश नहीं मिलेंगे।', ar: 'لن تصلك رسائل هذه المجموعة.' },
            okText: { vi: 'Rời nhóm', en: 'Leave', fr: 'Quitter', es: 'Salir', id: 'Keluar', ja: '退出', zh: '退出', ko: '나가기', hi: 'छोड़ें', ar: 'مغادرة' }, danger: true,
            onOk: function () { runChatAction('actionLeave', { id: chat.id }, function () { closeChat(); loadChats(0); }); }
        });
    }, [me, runChatAction, closeChat, loadChats]);

    var deleteGroup = useCallback(function () {
        var chat = activeRef.current;
        setConfirm({
            title: { vi: 'Xoá nhóm', en: 'Delete group', fr: 'Effacer groupe', es: 'Eliminar grupo', id: 'Hapus grup', ja: 'グループ削除', zh: '删除群组', ko: '그룹 삭제', hi: 'समूह हटाएँ', ar: 'حذف المجموعة' },
            message: { vi: 'Toàn bộ tin nhắn của nhóm sẽ bị xoá với mọi thành viên.', en: 'All messages are deleted for every member.', fr: 'Tous les messages seront supprimés pour tous les membres.', es: 'Se eliminarán todos los mensajes para todos los miembros.', id: 'Semua pesan grup akan dihapus untuk semua anggota.', ja: 'グループの全メッセージが全員から削除されます。', zh: '群内所有消息将对全体成员删除。', ko: '그룹의 모든 메시지가 모든 멤버에게서 삭제됩니다.', hi: 'समूह के सभी संदेश सभी सदस्यों के लिए हट जाएँगे।', ar: 'ستُحذف كل رسائل المجموعة لدى جميع الأعضاء.' },
            okText: { vi: 'Xoá nhóm', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' }, danger: true,
            onOk: function () { runChatAction('delete', { id: chat.id }, function () { closeChat(); loadChats(0); }); }
        });
    }, [runChatAction, closeChat, loadChats]);

    var refreshActive = useCallback(function () {
        var chat = activeRef.current;
        if (!chat) return;
        call(chatAPI, 'list', { code: chat.code, pageSize: 1 }).then(function (d) {
            var fresh = rowsOf(d).find(function (r) { return r.code === chat.code; });
            if (fresh) setActive(fresh);
        }).catch(function () { /* stays as is */ });
        loadChats(0);
    }, [loadChats]);

    // ─── Files ────────────────────────────────────────────────
    var addFiles = useCallback(function (list) {
        var picked = Array.prototype.slice.call(list || []);
        if (picked.length) setFiles(function (prev) { return prev.concat(picked); });
    }, []);

    var openFile = useCallback(function (message, pool) {
        var source = (pool || messagesRef.current).filter(function (m) { return !m.is_deleted && (m.meddoc_id || m.file_full_path) && (m.file_full_path || m.file_short_path); });
        var list = source.map(function (m) { var f = fileOf(m); return { name: f.name, url: f.url }; });
        var index = Math.max(0, source.findIndex(function (m) { return m.id === message.id; }));
        if (list.length) setViewer({ files: list, index: index });
    }, []);

    // ─── Render: list column ──────────────────────────────────
    function renderChatRow(c) {
        var id = String(c.id);
        var unread = props.unreadChatIds ? !!props.unreadChatIds[id] : !!c.has_unread && !readIds[id];
        if (active && String(active.id) === id) unread = false;
        var selected = active && String(active.id) === id;
        return (
            <button type="button" key={id} className={'cw-row' + (selected ? ' selected' : '') + (unread ? ' unread' : '')} onClick={function () { openChat(c); }}>
                <ChatAvatar chat={c} me={me} size={44} />
                <span className="cw-row-body">
                    <span className="cw-row-top">
                        <span className="cw-row-title">{titleOf(c, me)}</span>
                        {c.my_pinned && <span className="cw-row-pin" title={T({ vi: 'Đã ghim', en: 'Pinned', fr: 'Épinglé', es: 'Fijado', id: 'Disematkan', ja: 'ピン留め', zh: '已置顶', ko: '고정됨', hi: 'पिन किया', ar: 'مثبّت' })}><Icon name="pin" size={13} /></span>}
                        {c.last_message_at && <span className="cw-row-time">{sidebarRelativeTime(c.last_message_at)}</span>}
                    </span>
                    <span className="cw-row-bottom">
                        <span className="cw-row-preview">
                            {c.my_hidden && <span className="cw-tag">{T({ vi: 'Đã ẩn', en: 'Hidden', fr: 'Masqué', es: 'Oculto', id: 'Tersembunyi', ja: '非表示', zh: '已隐藏', ko: '숨김', hi: 'छिपा', ar: 'مخفي' })}</span>}
                            {c.last_message_content
                                ? (c.type !== 'direct' && c.last_message_user_name ? c.last_message_user_name + ': ' : '') + c.last_message_content
                                : T({ vi: 'Chưa có tin nhắn', en: 'No messages yet', fr: 'Aucun message', es: 'Sin mensajes', id: 'Belum ada pesan', ja: 'メッセージなし', zh: '暂无消息', ko: '메시지 없음', hi: 'कोई संदेश नहीं', ar: 'لا توجد رسائل' })}
                        </span>
                        {unread && <span className="cw-dot" aria-label={T({ vi: 'Chưa đọc', en: 'Unread', fr: 'Non lu', es: 'No leído', id: 'Belum baca', ja: '未読', zh: '未读', ko: '안 읽음', hi: 'अपठित', ar: 'غير مقروء' })} />}
                    </span>
                </span>
            </button>
        );
    }

    function renderList() {
        if (view === 'create') return <CreateChat me={me} onBack={function () { setView('list'); }} onCreated={function (chat) { setView('list'); loadChats(0); openChat(chat); }} onError={flash} />;
        return (
            <div className="cw-column">
                <div className="cw-list-bar">
                    <label className="cw-search">
                        <Icon name="search" size={16} />
                        <input value={search} onChange={function (e) { setSearch(e.target.value); }}
                            placeholder={T({ vi: 'Tìm tên, thành viên, nội dung', en: 'Search names, members, messages', fr: 'Chercher nom, membre, message', es: 'Buscar nombre, miembro, mensaje', id: 'Cari nama, anggota, pesan', ja: '名前・メンバー・内容を検索', zh: '搜索名称、成员、内容', ko: '이름, 멤버, 내용 검색', hi: 'नाम, सदस्य, संदेश खोजें', ar: 'بحث بالاسم أو العضو أو الرسالة' })} />
                        {search && <button type="button" className="cw-search-clear" onClick={function () { setSearch(''); }} aria-label={T({ vi: 'Xoá tìm kiếm', en: 'Clear search', fr: 'Effacer', es: 'Borrar búsqueda', id: 'Hapus pencarian', ja: '検索をクリア', zh: '清除搜索', ko: '검색 지우기', hi: 'खोज साफ़ करें', ar: 'مسح البحث' })}><Icon name="close" size={14} /></button>}
                    </label>
                    <IconButton icon="edit" label={{ vi: 'Trao đổi mới', en: 'New chat', fr: 'Nouveau chat', es: 'Nuevo chat', id: 'Chat baru', ja: '新規チャット', zh: '新聊天', ko: '새 채팅', hi: 'नई चैट', ar: 'محادثة جديدة' }} className="cw-icon-btn--accent" onClick={function () { setView('create'); }} />
                </div>
                <div className="cw-scroll">
                    {listState === 'loading' && <SidebarState type="loading" />}
                    {listState === 'error' && <SidebarState type="error" text={{ vi: 'Không tải được danh sách trao đổi', en: 'Could not load chats', fr: 'Échec du chargement des chats', es: 'No se pudieron cargar los chats', id: 'Gagal memuat daftar chat', ja: 'チャットを読み込めません', zh: '无法加载聊天列表', ko: '채팅 목록을 불러올 수 없습니다', hi: 'चैट लोड नहीं हो सकीं', ar: 'تعذر تحميل المحادثات' }} onRetry={function () { loadChats(0); }} />}
                    {listState === 'ready' && !chats.length && (
                        <SidebarState icon="note" text={search.trim()
                            ? { vi: 'Không tìm thấy trao đổi phù hợp', en: 'No matching chats', fr: 'Aucun chat correspondant', es: 'Sin chats coincidentes', id: 'Tidak ada chat yang cocok', ja: '該当するチャットがありません', zh: '没有匹配的聊天', ko: '일치하는 채팅이 없습니다', hi: 'कोई मेल खाती चैट नहीं', ar: 'لا توجد محادثات مطابقة' }
                            : { vi: 'Chưa có trao đổi. Bấm nút bút để bắt đầu với đồng nghiệp.', en: 'No chats yet. Use the pen button to start one.', fr: 'Aucun chat. Utilisez le bouton stylo pour en démarrer un.', es: 'Sin chats. Use el botón del lápiz para iniciar uno.', id: 'Belum ada chat. Tekan tombol pena untuk memulai.', ja: 'チャットはまだありません。ペンボタンで開始できます。', zh: '暂无聊天。点击笔形按钮与同事开始聊天。', ko: '채팅이 없습니다. 펜 버튼을 눌러 시작하세요.', hi: 'अभी कोई चैट नहीं। शुरू करने के लिए पेन बटन दबाएँ।', ar: 'لا محادثات بعد. اضغط زر القلم لبدء محادثة.' }} />
                    )}
                    {listState !== 'error' && chats.map(renderChatRow)}
                    {listState === 'ready' && listMore && <div className="cw-more"><button type="button" className="rs-more-btn" onClick={function () { loadChats(listPage.current + 1); }}>{T({ vi: 'Xem thêm', en: 'Load more', fr: 'Voir plus', es: 'Ver más', id: 'Lihat lagi', ja: 'もっと見る', zh: '查看更多', ko: '더 보기', hi: 'और देखें', ar: 'عرض المزيد' })}</button></div>}
                </div>
            </div>
        );
    }

    // ─── Render: detail column ────────────────────────────────
    function renderMessage(m, i) {
        var prev = messages[i - 1], mine = m.user_code === me;
        var newDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
        var grouped = !newDay && prev && !prev.is_deleted && prev.user_code === m.user_code && toMillis(m.created_at) - toMillis(prev.created_at) < 5 * 60000;
        var profile = m.user_profile || {};
        var name = profile.display_name || m.entity_full_name || m.user_name || '';
        var avatar = profile.avatar_full_path || m.avatar_full_path;
        var likes = likedBy(m), liked = likes.indexOf(me) >= 0, likeCount = Number(m.liked_count) || likes.length;
        var file = m.content_type === 'file' || m.meddoc_id ? fileOf(m) : null;
        var divider = newDay && <div className="cw-day"><span>{dayLabel(m.created_at)}</span></div>;
        if (m.content_type === 'system') return <Fragment key={m.id}>{divider}<div className="cw-system">{m.content}</div></Fragment>;
        return (
            <Fragment key={m.id}>
                {divider}
                <div className={'cw-msg' + (mine ? ' mine' : '') + (grouped ? ' grouped' : '')}>
                    {!mine && <span className="cw-msg-avatar">{!grouped && <Avatar src={avatar} name={name} size={32} />}</span>}
                    <div className="cw-msg-main">
                        {!mine && !grouped && active.type !== 'direct' && <div className="cw-msg-author">{name}</div>}
                        {m.is_deleted ? (
                            <div className="cw-bubble cw-bubble--deleted">{T({ vi: 'Tin nhắn đã bị xoá', en: 'Message deleted', fr: 'Message supprimé', es: 'Mensaje eliminado', id: 'Pesan dihapus', ja: '削除されたメッセージ', zh: '消息已删除', ko: '삭제된 메시지', hi: 'संदेश हटाया गया', ar: 'تم حذف الرسالة' })}</div>
                        ) : (
                            <div className="cw-msg-line">
                                <div className={'cw-bubble' + (file && isImage(file) && !m.reply_message_id ? ' cw-bubble--media' : '')} onContextMenu={function (e) { openMessageMenu(e, m); }}>
                                    {m.reply_message_id && (
                                        <div className="cw-quote">
                                            <span className="cw-quote-name">{m.reply_user_name}</span>
                                            <span className="cw-quote-text">{m.reply_content || T({ vi: 'Tin nhắn đã bị xoá', en: 'Message deleted', fr: 'Message supprimé', es: 'Mensaje eliminado', id: 'Pesan dihapus', ja: '削除されたメッセージ', zh: '消息已删除', ko: '삭제된 메시지', hi: 'संदेश हटाया गया', ar: 'تم حذف الرسالة' })}</span>
                                        </div>
                                    )}
                                    {file ? (file.url ? (isImage(file)
                                        ? <button type="button" className="cw-image" onClick={function () { openFile(m); }}><img src={file.url} alt={file.name} loading="lazy" /></button>
                                        : <button type="button" className="cw-file" onClick={function () { openFile(m); }}>
                                            <span className="cw-file-ext">{fileExt(file.name)}</span>
                                            <span className="cw-file-body"><span className="cw-file-name">{file.name}</span><span className="cw-file-meta">{fileSize(file.size)}</span></span>
                                        </button>)
                                        : <span className="cw-file-missing">{file.name} · {T({ vi: 'tệp không còn', en: 'file removed', fr: 'fichier supprimé', es: 'archivo borrado', id: 'berkas dihapus', ja: 'ファイル削除済み', zh: '文件已删除', ko: '파일 삭제됨', hi: 'फ़ाइल हटाई गई', ar: 'الملف محذوف' })}</span>)
                                        : <div className="cw-text">{linkify(m.content)}</div>}
                                    <span className="cw-time">{sidebarTime(m.created_at)}</span>
                                </div>
                                <div className="cw-msg-tools">
                                    <IconButton icon="reply" size={16} label={{ vi: 'Trả lời', en: 'Reply', fr: 'Répondre', es: 'Responder', id: 'Balas', ja: '返信', zh: '回复', ko: '답글', hi: 'जवाब दें', ar: 'رد' }} onClick={function () { startReply(m); }} />
                                    <IconButton icon="more" size={16} label={{ vi: 'Thêm', en: 'More', fr: 'Plus', es: 'Más', id: 'Lainnya', ja: 'その他', zh: '更多', ko: '더보기', hi: 'अधिक', ar: 'المزيد' }} onClick={function (e) { openMessageMenu(e, m); }} />
                                </div>
                            </div>
                        )}
                        {!m.is_deleted && (
                            <div className="cw-reactions">
                                <button type="button" className={'cw-like' + (liked ? ' liked' : '') + (likeCount ? ' has-likes' : '')} onClick={function () { toggleLike(m); }}
                                    aria-pressed={liked} title={T({ vi: 'Thích', en: 'Like', fr: 'J\'aime', es: 'Me gusta', id: 'Suka', ja: 'いいね', zh: '赞', ko: '좋아요', hi: 'पसंद', ar: 'إعجاب' })}>
                                    <Icon name="heart" size={15} fill={liked ? 'currentColor' : 'none'} />
                                    {likeCount > 0 && <span>{likeCount}</span>}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </Fragment>
        );
    }

    function renderComposer() {
        return (
            <div className="cw-composer">
                {reply && (
                    <div className="cw-reply-bar">
                        <Icon name="reply" size={16} />
                        <div className="cw-reply-body">
                            <span className="cw-reply-title">{T({ vi: 'Trả lời', en: 'Replying to', fr: 'Réponse à', es: 'Respuesta a', id: 'Membalas', ja: '返信先', zh: '回复', ko: '답장', hi: 'उत्तर', ar: 'رد على' })} {(reply.user_profile && reply.user_profile.display_name) || reply.user_name}</span>
                            <span className="cw-reply-text">{reply.content_type === 'file' ? fileOf(reply).name : reply.content}</span>
                        </div>
                        <IconButton icon="close" size={15} label={{ vi: 'Huỷ trả lời', en: 'Cancel reply', fr: 'Annuler réponse', es: 'Cancelar', id: 'Batal balas', ja: '返信を取消', zh: '取消回复', ko: '답장 취소', hi: 'जवाब रद्द करें', ar: 'إلغاء الرد' }} onClick={function () { setReply(null); }} />
                    </div>
                )}
                {files.length > 0 && (
                    <div className="cw-pending">
                        {files.map(function (f, i) {
                            return (
                                <span key={i} className="cw-pending-file">
                                    <Icon name="attach" size={14} />
                                    <span className="cw-pending-name">{f.name}</span>
                                    <button type="button" onClick={function () { setFiles(function (prev) { return prev.filter(function (_, x) { return x !== i; }); }); }}
                                        aria-label={T({ vi: 'Bỏ tệp', en: 'Remove file', fr: 'Retirer', es: 'Quitar archivo', id: 'Hapus berkas', ja: 'ファイル削除', zh: '移除文件', ko: '파일 제거', hi: 'फ़ाइल हटाएँ', ar: 'إزالة الملف' })}><Icon name="close" size={13} /></button>
                                </span>
                            );
                        })}
                    </div>
                )}
                <div className="cw-compose-row">
                    <input ref={fileRef} type="file" multiple hidden onChange={function (e) { addFiles(e.target.files); e.target.value = ''; }} />
                    <IconButton icon="attach" label={{ vi: 'Đính kèm tệp', en: 'Attach files', fr: 'Joindre fichier', es: 'Adjuntar', id: 'Lampirkan file', ja: 'ファイル添付', zh: '添加附件', ko: '파일 첨부', hi: 'फ़ाइल जोड़ें', ar: 'إرفاق ملف' }} onClick={function () { if (fileRef.current) fileRef.current.click(); }} disabled={sending} />
                    <div className="cw-input-wrap">
                        <textarea ref={inputRef} className="cw-input" rows={1} value={text} onChange={onType} onKeyDown={onKeyDown}
                            placeholder={T({ vi: 'Nhập tin nhắn...', en: 'Type a message...', fr: 'Entrez un message...', es: 'Ingrese mensaje...', id: 'Masukkan pesan...', ja: 'メッセージを入力...', zh: '输入消息...', ko: '메시지 입력...', hi: 'संदेश दर्ज करें...', ar: 'أدخل رسالة...' })} disabled={sending} />
                        <IconButton icon="smile" label={{ vi: 'Biểu tượng cảm xúc', en: 'Emoji', fr: 'Emoji', es: 'Emoji', id: 'Emoji', ja: '絵文字', zh: '表情', ko: '이모지', hi: 'इमोजी', ar: 'رموز تعبيرية' }} active={emojiOpen} onClick={function () { setEmojiOpen(function (v) { return !v; }); }} />
                    </div>
                    <button type="button" className="cw-send" onClick={send} disabled={sending || (!text.trim() && !files.length)} aria-label={T({ vi: 'Gửi', en: 'Send', fr: 'Envoyer', es: 'Enviar', id: 'Kirim', ja: '送信', zh: '发送', ko: '보내기', hi: 'भेजें', ar: 'إرسال' })}>
                        {sending ? <span className="rs-spinner" /> : <Icon name="send" size={17} />}
                    </button>
                    {emojiOpen && (
                        <EmojiPicker onClose={function () { setEmojiOpen(false); }} onPick={function (emoji) {
                            var el = inputRef.current, start = el ? el.selectionStart : text.length, end = el ? el.selectionEnd : text.length;
                            setText(text.slice(0, start) + emoji + text.slice(end));
                            setTimeout(function () { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + emoji.length; } }, 0);
                        }} />
                    )}
                </div>
                <div className="cw-hint">{T({ vi: 'Enter để gửi · Shift + Enter xuống dòng', en: 'Enter to send · Shift + Enter for a new line', fr: 'Entrée : envoyer · Maj + Entrée : nouvelle ligne', es: 'Enter para enviar · Shift + Enter: nueva línea', id: 'Enter untuk kirim · Shift + Enter baris baru', ja: 'Enterで送信 · Shift + Enterで改行', zh: 'Enter 发送 · Shift + Enter 换行', ko: 'Enter 전송 · Shift + Enter 줄바꿈', hi: 'Enter से भेजें · Shift + Enter से नई पंक्ति', ar: 'Enter للإرسال · Shift + Enter لسطر جديد' })}</div>
            </div>
        );
    }

    function renderDetail() {
        if (!active) {
            return (
                <div className="cw-column cw-empty-detail">
                    <span className="cw-empty-icon"><Icon name="chat" size={30} stroke={1.5} /></span>
                    <div className="cw-empty-title">{T({ vi: 'Chọn một cuộc trao đổi', en: 'Select a conversation', fr: 'Sélectionnez un chat', es: 'Seleccione un chat', id: 'Pilih percakapan', ja: 'チャットを選択', zh: '选择一个聊天', ko: '채팅을 선택하세요', hi: 'कोई चैट चुनें', ar: 'اختر محادثة' })}</div>
                    <div className="cw-empty-text">{T({ vi: 'Hoặc bắt đầu trao đổi mới với đồng nghiệp', en: 'Or start a new chat with your colleagues', fr: 'Ou démarrez un chat avec vos collègues', es: 'O inicie un chat nuevo con sus colegas', id: 'Atau mulai chat baru dengan rekan kerja', ja: 'または同僚と新しいチャットを開始', zh: '或与同事开始新的聊天', ko: '또는 동료와 새 채팅을 시작하세요', hi: 'या सहकर्मियों से नई चैट शुरू करें', ar: 'أو ابدأ محادثة جديدة مع زملائك' })}</div>
                    <button type="button" className="cw-btn cw-btn--primary" onClick={function () { setView('create'); }}><Icon name="edit" size={16} />{T({ vi: 'Trao đổi mới', en: 'New chat', fr: 'Nouveau chat', es: 'Nuevo chat', id: 'Chat baru', ja: '新規チャット', zh: '新聊天', ko: '새 채팅', hi: 'नई चैट', ar: 'محادثة جديدة' })}</button>
                </div>
            );
        }
        var isGroup = active.type !== 'direct';
        if (sub === 'members' || sub === 'add') {
            return <Members chat={active} me={me} adding={sub === 'add'} onBack={function () { setSub(sub === 'add' ? 'members' : 'messages'); }}
                onAdd={function () { setSub('add'); }} onLeave={leaveGroup} onDelete={deleteGroup} onChanged={refreshActive} onError={flash} setConfirm={setConfirm} />;
        }
        if (sub === 'media') return <Media chat={active} onBack={function () { setSub('messages'); }} onOpenFile={openFile} />;
        var members = membersOf(active);
        var subtitle = isGroup ? members.length + ' ' + T({ vi: 'thành viên', en: 'members', fr: 'membres', es: 'miembros', id: 'anggota', ja: '人', zh: '位成员', ko: '명', hi: 'सदस्य', ar: 'أعضاء' }) : T({ vi: 'Trao đổi 1-1', en: 'Direct chat', fr: 'Chat direct', es: 'Chat directo', id: 'Chat langsung', ja: '1対1チャット', zh: '单聊', ko: '1:1 채팅', hi: 'सीधी चैट', ar: 'محادثة مباشرة' });
        return (
            <div className={'cw-column cw-detail' + (dragOver ? ' drag-over' : '')}
                onDragOver={function (e) { if (e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types || [], 'Files') >= 0) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={function (e) { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
                onDrop={function (e) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}>
                <div className="cw-header">
                    {mode === 'sidebar' && <IconButton icon="back" label={{ vi: 'Quay lại', en: 'Back', fr: 'Retour', es: 'Volver', id: 'Kembali', ja: '戻る', zh: '返回', ko: '뒤로', hi: 'वापस', ar: 'رجوع' }} onClick={closeChat} />}
                    <button type="button" className="cw-header-identity" onClick={function () { setSub('members'); }}>
                        <ChatAvatar chat={active} me={me} size={36} />
                        <span className="cw-header-text">
                            <span className="cw-header-title">{titleOf(active, me)}</span>
                            <span className="cw-header-sub">{subtitle}</span>
                        </span>
                    </button>
                    {active.my_pinned && <span className="cw-header-pin" title={T({ vi: 'Đã ghim', en: 'Pinned', fr: 'Épinglé', es: 'Fijado', id: 'Disematkan', ja: 'ピン留め', zh: '已置顶', ko: '고정됨', hi: 'पिन किया', ar: 'مثبّت' })}><Icon name="pin" size={15} /></span>}
                    <IconButton icon="info" label={{ vi: 'Thông tin trò chuyện', en: 'Chat info', fr: 'Infos du chat', es: 'Info del chat', id: 'Info chat', ja: 'チャット情報', zh: '聊天信息', ko: '채팅 정보', hi: 'चैट जानकारी', ar: 'معلومات المحادثة' }} active={!!infoMenu}
                        onClick={function (e) { var r = e.currentTarget.getBoundingClientRect(); setInfoMenu({ x: r.right, y: r.bottom + 6 }); }} />
                </div>
                <div className="cw-messages" ref={scrollRef} onScroll={function (e) {
                    var el = e.currentTarget;
                    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                    if (el.scrollTop < 60 && msgMore && !loadingOlder) loadMessages(active, true);
                }}>
                    {msgMore && <div className="cw-older">{loadingOlder ? <span className="rs-spinner" /> : <button type="button" onClick={function () { loadMessages(active, true); }}>{T({ vi: 'Xem tin nhắn cũ hơn', en: 'Load older messages', fr: 'Messages plus anciens', es: 'Ver mensajes anteriores', id: 'Lihat pesan lama', ja: '以前のメッセージ', zh: '查看更早消息', ko: '이전 메시지 보기', hi: 'पुराने संदेश देखें', ar: 'عرض الرسائل الأقدم' })}</button>}</div>}
                    {msgState === 'loading' && <SidebarState type="loading" />}
                    {msgState === 'error' && <SidebarState type="error" text={{ vi: 'Không tải được tin nhắn', en: 'Could not load messages', fr: 'Échec du chargement', es: 'Error al cargar mensajes', id: 'Gagal memuat pesan', ja: 'メッセージを読み込めません', zh: '无法加载消息', ko: '메시지를 불러올 수 없습니다', hi: 'संदेश लोड नहीं हो सके', ar: 'تعذر تحميل الرسائل' }} onRetry={function () { loadMessages(active); }} />}
                    {msgState === 'ready' && !messages.length && (
                        <div className="cw-start">
                            <ChatAvatar chat={active} me={me} size={56} />
                            <div className="cw-start-title">{titleOf(active, me)}</div>
                            <div className="cw-start-text">{T({ vi: 'Hãy gửi lời chào để bắt đầu trao đổi', en: 'Say hello to start the conversation', fr: 'Dites bonjour pour démarrer le chat', es: 'Salude para iniciar la conversación', id: 'Sapa untuk memulai percakapan', ja: '挨拶してチャットを始めましょう', zh: '打个招呼开始聊天吧', ko: '인사를 보내 대화를 시작하세요', hi: 'बातचीत शुरू करने के लिए नमस्ते कहें', ar: 'ألقِ التحية لبدء المحادثة' })}</div>
                        </div>
                    )}
                    {messages.map(renderMessage)}
                    {typing.length > 0 && (
                        <div className="cw-typing"><span className="cw-typing-dots"><i /><i /><i /></span>
                            {typing.map(function (u) { return u.name || u.code; }).join(', ')} {T({ vi: 'đang nhập...', en: 'is typing...', fr: 'écrit...', es: 'escribe...', id: 'mengetik...', ja: 'が入力中...', zh: '正在输入...', ko: '입력 중...', hi: 'लिख रहे हैं...', ar: 'يكتب...' })}</div>
                    )}
                </div>
                {dragOver && <div className="cw-drop"><Icon name="attach" size={26} /><span>{T({ vi: 'Thả tệp để đính kèm', en: 'Drop files to attach', fr: 'Déposez pour joindre', es: 'Suelte para adjuntar', id: 'Lepas untuk melampirkan', ja: 'ドロップして添付', zh: '拖放文件以添加附件', ko: '파일을 놓아 첨부', hi: 'संलग्न करने हेतु छोड़ें', ar: 'أفلت الملفات للإرفاق' })}</span></div>}
                {renderComposer()}
            </div>
        );
    }

    // ─── Overlays shared by all modes ─────────────────────────
    var overlays = (
        <Fragment>
            {notice && <div className={'cw-toast' + (mode === 'floating' ? ' cw-toast--fixed' : '')} role="status">{notice}</div>}
            <ConfirmDialog state={confirm} onCancel={function () { setConfirm(null); }} onConfirm={function () { var ok = confirm.onOk; setConfirm(null); ok(); }} />
            {viewer && <AntFileViewer visible files={viewer.files} initialIndex={viewer.index} onClose={function () { setViewer(null); }} />}
            {infoMenu && active && (
                <PopMenu x={infoMenu.x} y={infoMenu.y} alignRight onClose={function () { setInfoMenu(null); }} items={[
                    { icon: 'pin', label: active.my_pinned ? { vi: 'Bỏ ghim', en: 'Unpin', fr: 'Détacher', es: 'Desfijar', id: 'Lepas pin', ja: 'ピン解除', zh: '取消置顶', ko: '고정 해제', hi: 'अनपिन करें', ar: 'فك التثبيت' } : { vi: 'Ghim trò chuyện', en: 'Pin chat', fr: 'Épingler', es: 'Fijar chat', id: 'Sematkan chat', ja: 'ピン留め', zh: '置顶聊天', ko: '채팅 고정', hi: 'चैट पिन करें', ar: 'تثبيت المحادثة' }, onClick: function () { setPreference({ pinned: !active.my_pinned }); } },
                    { icon: active.my_hidden ? 'eye' : 'eyeOff', label: active.my_hidden ? { vi: 'Bỏ ẩn', en: 'Unhide', fr: 'Afficher', es: 'Mostrar', id: 'Tampilkan', ja: '再表示', zh: '取消隐藏', ko: '숨김 해제', hi: 'दिखाएँ', ar: 'إظهار' } : { vi: 'Ẩn trò chuyện', en: 'Hide chat', fr: 'Masquer le chat', es: 'Ocultar chat', id: 'Sembunyikan chat', ja: 'チャットを非表示', zh: '隐藏聊天', ko: '채팅 숨기기', hi: 'चैट छिपाएँ', ar: 'إخفاء المحادثة' }, onClick: function () { setPreference({ hidden: !active.my_hidden }); } },
                    { divider: true },
                    { icon: 'users', label: { vi: 'Xem thành viên', en: 'Members', fr: 'Membres', es: 'Ver miembros', id: 'Lihat anggota', ja: 'メンバーを表示', zh: '查看成员', ko: '멤버 보기', hi: 'सदस्य देखें', ar: 'عرض الأعضاء' }, onClick: function () { setSub('members'); } },
                    { icon: 'media', label: { vi: 'Ảnh, file, link', en: 'Photos, files, links', fr: 'Photos, fichiers, liens', es: 'Fotos, archivos, links', id: 'Foto, berkas, tautan', ja: '写真・ファイル・リンク', zh: '图片、文件、链接', ko: '사진, 파일, 링크', hi: 'फ़ोटो, फ़ाइलें, लिंक', ar: 'صور، ملفات، روابط' }, onClick: function () { setSub('media'); } },
                    { divider: true },
                    { icon: 'sweep', label: { vi: 'Xoá lịch sử', en: 'Clear history', fr: 'Vider historique', es: 'Borrar historial', id: 'Hapus riwayat', ja: '履歴を消去', zh: '清除记录', ko: '기록 지우기', hi: 'इतिहास मिटाएँ', ar: 'مسح السجل' }, danger: true, onClick: clearHistory }
                ]} />
            )}
            {msgMenu && (
                <PopMenu x={msgMenu.x} y={msgMenu.y} alignRight={msgMenu.alignRight} flipOffset={msgMenu.flipOffset} onClose={function () { setMsgMenu(null); }} items={[
                    { icon: 'reply', label: { vi: 'Trả lời', en: 'Reply', fr: 'Répondre', es: 'Responder', id: 'Balas', ja: '返信', zh: '回复', ko: '답글', hi: 'जवाब दें', ar: 'رد' }, onClick: function () { startReply(msgMenu.message); } },
                    msgMenu.message.content_type !== 'file' && msgMenu.message.content && { icon: 'copy', label: { vi: 'Sao chép', en: 'Copy', fr: 'Copier', es: 'Copiar', id: 'Salin', ja: 'コピー', zh: '复制', ko: '복사', hi: 'कॉपी करें', ar: 'نسخ' }, onClick: function () { copyMessage(msgMenu.message); } },
                    msgMenu.message.user_code === me && { divider: true },
                    msgMenu.message.user_code === me && { icon: 'trash', label: { vi: 'Xoá', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' }, danger: true, onClick: function () { deleteMessage(msgMenu.message); } }
                ]} />
            )}
        </Fragment>
    );

    if (mode === 'floating') {
        return (
            <Fragment>
                {overlays}
                <FloatingWorkPanel visible={props.visible} onClose={props.onClose} title={T({ vi: 'Trao đổi', en: 'Chat', fr: 'Chat', es: 'Chat', id: 'Chat', ja: 'チャット', zh: '聊天', ko: '채팅', hi: 'चैट', ar: 'محادثة' })} icon={ICONS.chat}
                    renderList={renderList} renderDetail={renderDetail} selectedItem={active} onSelectItem={openChat}
                    listWidth={320} minWidth={820} maxWidth={1180} className="cw-floating" />
            </Fragment>
        );
    }
    if (mode === 'embed') {
        return (
            <div className="cw cw--embed">
                {overlays}
                <div className="cw-embed-list">{renderList()}</div>
                <div className="cw-embed-detail">{renderDetail()}</div>
            </div>
        );
    }
    return (
        <div className="cw cw--sidebar">
            {overlays}
            {active ? renderDetail() : renderList()}
        </div>
    );
}

// ─── Emoji picker ─────────────────────────────────────────────
function EmojiPicker(props) {
    var ref = useRef(null);
    useEffect(function () {
        function onDown(e) { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.cw-input-wrap')) props.onClose(); }
        document.addEventListener('mousedown', onDown);
        return function () { document.removeEventListener('mousedown', onDown); };
    }, [props.onClose]);
    return (
        <div className="cw-emoji" ref={ref}>
            {EMOJIS.map(function (emoji) {
                return <button type="button" key={emoji} onClick={function () { props.onPick(emoji); }}>{emoji}</button>;
            })}
        </div>
    );
}

// ─── Sub view header ──────────────────────────────────────────
function SubHeader(props) {
    return (
        <div className="cw-header cw-header--sub">
            <IconButton icon="back" label={{ vi: 'Quay lại', en: 'Back', fr: 'Retour', es: 'Volver', id: 'Kembali', ja: '戻る', zh: '返回', ko: '뒤로', hi: 'वापस', ar: 'رجوع' }} onClick={props.onBack} />
            <span className="cw-header-title">{T(props.title)}</span>
            {props.right}
        </div>
    );
}

// ─── People picker (new chat, add member) ─────────────────────
function usePeopleSearch(me, exclude) {
    var [query, setQuery] = useState('');
    var [people, setPeople] = useState([]);
    var [state, setState] = useState('loading');
    var version = useRef(0);
    useEffect(function () {
        var current = ++version.current;
        var timer = setTimeout(function () {
            setState('loading');
            call(userAPI, 'list', { pageSize: 50, pageIndex: 0, searchText: query.trim() || undefined }).then(function (data) {
                if (current !== version.current) return;
                setPeople(rowsOf(data).filter(function (u) { return u.code && u.code !== me && (!exclude || exclude.indexOf(u.code) < 0) && u.is_active !== 0; }));
                setState('ready');
            }).catch(function () { if (current === version.current) setState('error'); });
        }, 250);
        return function () { clearTimeout(timer); };
    }, [query, me, exclude]);
    return { query: query, setQuery: setQuery, people: people, state: state };
}

function personName(u) {
    return u.entity_full_name || u.entity_name || u.name || u.code;
}

function PeopleList(props) {
    var search = props.search;
    return (
        <Fragment>
            <div className="cw-list-bar">
                <label className="cw-search">
                    <Icon name="search" size={16} />
                    <input autoFocus value={search.query} onChange={function (e) { search.setQuery(e.target.value); }} placeholder={T({ vi: 'Tìm đồng nghiệp', en: 'Find colleagues', fr: 'Chercher collègues', es: 'Buscar colegas', id: 'Cari rekan kerja', ja: '同僚を検索', zh: '查找同事', ko: '동료 찾기', hi: 'सहकर्मी खोजें', ar: 'بحث عن زملاء' })} />
                </label>
            </div>
            <div className="cw-scroll">
                {search.state === 'loading' && !search.people.length && <SidebarState type="loading" />}
                {search.state === 'error' && <SidebarState type="error" text={{ vi: 'Không tải được danh sách người dùng', en: 'Could not load people', fr: 'Échec du chargement des utilisateurs', es: 'No se pudieron cargar los usuarios', id: 'Gagal memuat daftar pengguna', ja: 'ユーザーを読み込めません', zh: '无法加载用户列表', ko: '사용자 목록을 불러올 수 없습니다', hi: 'उपयोगकर्ता लोड नहीं हो सके', ar: 'تعذر تحميل المستخدمين' }} />}
                {search.state === 'ready' && !search.people.length && <SidebarState text={{ vi: 'Không tìm thấy người phù hợp', en: 'No one found', fr: 'Aucun résultat', es: 'Sin resultados', id: 'Tidak ditemukan', ja: '該当者なし', zh: '未找到匹配的人', ko: '일치하는 사람 없음', hi: 'कोई नहीं मिला', ar: 'لم يُعثر على أحد' }} />}
                {search.people.map(function (u) {
                    var picked = props.isPicked(u);
                    return (
                        <button type="button" key={u.code} className={'cw-person' + (picked ? ' picked' : '')} onClick={function () { props.onPick(u); }} disabled={props.busy}>
                            <Avatar src={u.avatar_full_path} name={personName(u)} size={38} />
                            <span className="cw-person-body"><span className="cw-person-name">{personName(u)}</span>{u.email && <span className="cw-person-meta">{u.email}</span>}</span>
                            {props.check && <span className={'cw-check' + (picked ? ' on' : '')}>{picked && <Icon name="check" size={13} stroke={3} />}</span>}
                            {props.action && <span className="cw-person-action">{T(props.action)}</span>}
                        </button>
                    );
                })}
            </div>
        </Fragment>
    );
}

function CreateChat(props) {
    var search = usePeopleSearch(props.me, null);
    var [picked, setPicked] = useState([]);
    var [name, setName] = useState('');
    var [busy, setBusy] = useState(false);
    var isGroup = picked.length > 1;
    var canCreate = picked.length > 0 && (!isGroup || name.trim()) && !busy;
    var toggle = function (u) {
        setPicked(function (prev) { return prev.some(function (p) { return p.code === u.code; }) ? prev.filter(function (p) { return p.code !== u.code; }) : prev.concat([u]); });
    };
    var create = function () {
        if (!canCreate) return;
        setBusy(true);
        call(chatAPI, 'create', { type: isGroup ? 'group' : 'direct', name: isGroup ? name.trim() : undefined, member_user_codes: picked.map(function (p) { return p.code; }) })
            .then(function (data) {
                var chat = data && (data.obj || data);
                if (!chat || !chat.id) throw new Error('Missing chat');
                props.onCreated(chat);
            })
            .catch(function () { props.onError({ vi: 'Chưa tạo được trao đổi, vui lòng thử lại', en: 'Could not start the chat, please try again', fr: 'Chat non créé, veuillez réessayer', es: 'No se pudo crear el chat, inténtelo de nuevo', id: 'Chat belum dibuat, silakan coba lagi', ja: 'チャットを作成できません。再試行してください', zh: '无法创建聊天，请重试', ko: '채팅을 만들지 못했습니다. 다시 시도하세요', hi: 'चैट नहीं बन सकी, पुनः प्रयास करें', ar: 'تعذر إنشاء المحادثة، حاول مرة أخرى' }); })
            .finally(function () { setBusy(false); });
    };
    return (
        <div className="cw-column">
            <SubHeader title={{ vi: 'Trao đổi mới', en: 'New chat', fr: 'Nouveau chat', es: 'Nuevo chat', id: 'Chat baru', ja: '新規チャット', zh: '新聊天', ko: '새 채팅', hi: 'नई चैट', ar: 'محادثة جديدة' }} onBack={props.onBack} />
            {picked.length > 0 && (
                <div className="cw-picked">
                    {picked.map(function (u) {
                        return (
                            <button type="button" key={u.code} className="cw-chip" onClick={function () { toggle(u); }} title={T({ vi: 'Bỏ chọn', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Hapus', ja: '解除', zh: '移除', ko: '제거', hi: 'हटाएँ', ar: 'إزالة' })}>
                                <Avatar src={u.avatar_full_path} name={personName(u)} size={20} /><span>{personName(u)}</span><Icon name="close" size={12} />
                            </button>
                        );
                    })}
                </div>
            )}
            {isGroup && (
                <div className="cw-field">
                    <input value={name} onChange={function (e) { setName(e.target.value); }} placeholder={T({ vi: 'Tên nhóm (bắt buộc)', en: 'Group name (required)', fr: 'Nom du groupe (requis)', es: 'Nombre grupo (requerido)', id: 'Nama grup (wajib)', ja: 'グループ名（必須）', zh: '群名称（必填）', ko: '그룹 이름 (필수)', hi: 'समूह नाम (आवश्यक)', ar: 'اسم المجموعة (مطلوب)' })} maxLength={120} />
                </div>
            )}
            <PeopleList search={search} check isPicked={function (u) { return picked.some(function (p) { return p.code === u.code; }); }} onPick={toggle} />
            <div className="cw-footer">
                <button type="button" className="cw-btn cw-btn--primary cw-btn--block" disabled={!canCreate} onClick={create}>
                    {busy ? <span className="rs-spinner" /> : null}
                    {T(isGroup ? { vi: 'Tạo nhóm', en: 'Create group', fr: 'Créer groupe', es: 'Crear grupo', id: 'Buat grup', ja: 'グループ作成', zh: '创建群组', ko: '그룹 만들기', hi: 'समूह बनाएँ', ar: 'إنشاء مجموعة' } : { vi: 'Bắt đầu trao đổi', en: 'Start chat', fr: 'Démarrer le chat', es: 'Iniciar chat', id: 'Mulai chat', ja: 'チャット開始', zh: '开始聊天', ko: '채팅 시작', hi: 'चैट शुरू करें', ar: 'بدء المحادثة' })}
                </button>
            </div>
        </div>
    );
}

// ─── Members ──────────────────────────────────────────────────
function Members(props) {
    var chat = props.chat, me = props.me;
    var isGroup = chat.type !== 'direct';
    var leader = leaderOf(chat), isLeader = isGroup && leader === me;
    var members = membersOf(chat);
    var [renaming, setRenaming] = useState(false);
    var [name, setName] = useState(chat.name || '');
    var [busy, setBusy] = useState(false);
    var exclude = useMemo(function () { return members.map(function (m) { return m.code; }); }, [chat]);

    var act = function (action, body) {
        setBusy(true);
        return call(chatAPI, action, body).then(function () { props.onChanged(); }).catch(function () { props.onError(); }).finally(function () { setBusy(false); });
    };
    var saveName = function () {
        if (!name.trim() || name.trim() === chat.name) { setRenaming(false); return; }
        act('update', { where: { id: chat.id }, update: { name: name.trim() } }).then(function () { setRenaming(false); });
    };
    var remove = function (m) {
        props.setConfirm({
            title: { vi: 'Mời khỏi nhóm', en: 'Remove member', fr: 'Retirer membre', es: 'Quitar miembro', id: 'Keluarkan', ja: 'メンバー削除', zh: '移出群组', ko: '멤버 내보내기', hi: 'सदस्य हटाएँ', ar: 'إزالة العضو' },
            message: { vi: (m.display_name || m.code) + ' sẽ không còn xem và nhận tin nhắn của nhóm.', en: (m.display_name || m.code) + ' will no longer see or receive group messages.' },
            okText: { vi: 'Mời khỏi nhóm', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Keluarkan', ja: '削除', zh: '移出', ko: '내보내기', hi: 'हटाएँ', ar: 'إزالة' }, danger: true,
            onOk: function () { act('actionRemoveMember', { id: chat.id, user_code: m.code }); }
        });
    };

    if (props.adding) {
        return <AddMember chat={chat} me={me} exclude={exclude} busy={busy} onBack={props.onBack} onAdd={function (u) { act('actionAddMember', { id: chat.id, user_code: u.code }).then(props.onBack); }} />;
    }
    return (
        <div className="cw-column">
            <SubHeader title={isGroup ? { vi: 'Thành viên', en: 'Members', fr: 'Membres', es: 'Miembros', id: 'Anggota', ja: 'メンバー', zh: '成员', ko: '멤버', hi: 'सदस्य', ar: 'الأعضاء' } : { vi: 'Thông tin trao đổi', en: 'Chat details', fr: 'Infos du chat', es: 'Info del chat', id: 'Info chat', ja: 'チャット情報', zh: '聊天信息', ko: '채팅 정보', hi: 'चैट जानकारी', ar: 'معلومات المحادثة' }} onBack={props.onBack} />
            <div className="cw-scroll cw-scroll--pad">
                {isGroup && (
                    <div className="cw-section">
                        <div className="cw-section-label">{T({ vi: 'Tên nhóm', en: 'Group name', fr: 'Nom du groupe', es: 'Nombre grupo', id: 'Nama grup', ja: 'グループ名', zh: '分组名称', ko: '그룹 이름', hi: 'समूह का नाम', ar: 'اسم المجموعة' })}</div>
                        {renaming ? (
                            <div className="cw-rename">
                                <input autoFocus value={name} maxLength={120} onChange={function (e) { setName(e.target.value); }}
                                    onKeyDown={function (e) { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setRenaming(false); }} />
                                <button type="button" className="cw-btn cw-btn--primary" disabled={busy || !name.trim()} onClick={saveName}>{T({ vi: 'Lưu', en: 'Save', es: 'Guardar', id: 'Simpan', ja: '保存', zh: '保存', ko: '저장', hi: 'सहेजें', ar: 'حفظ', fr: 'Enreg.' })}</button>
                            </div>
                        ) : (
                            <div className="cw-name-row">
                                <span className="cw-name-value">{chat.name}</span>
                                {isLeader && <IconButton icon="edit" size={16} label={{ vi: 'Đổi tên nhóm', en: 'Rename group', fr: 'Renommer', es: 'Renombrar grupo', id: 'Ubah nama grup', ja: 'グループ名変更', zh: '重命名群组', ko: '그룹 이름 변경', hi: 'समूह नाम बदलें', ar: 'إعادة التسمية' }} onClick={function () { setName(chat.name || ''); setRenaming(true); }} />}
                            </div>
                        )}
                    </div>
                )}
                <div className="cw-section">
                    <div className="cw-section-head">
                        <span className="cw-section-label">{members.length} {T({ vi: 'thành viên', en: 'members', fr: 'membres', es: 'miembros', id: 'anggota', ja: '人', zh: '位成员', ko: '명', hi: 'सदस्य', ar: 'أعضاء' })}</span>
                        {isLeader && <button type="button" className="cw-link-btn" onClick={props.onAdd}><Icon name="userPlus" size={16} />{T({ vi: 'Thêm thành viên', en: 'Add member', fr: 'Ajouter un membre', es: 'Añadir miembro', id: 'Tambah anggota', ja: 'メンバー追加', zh: '添加成员', ko: '멤버 추가', hi: 'सदस्य जोड़ें', ar: 'إضافة عضو' })}</button>}
                    </div>
                    {members.map(function (m) {
                        return (
                            <div key={m.code} className="cw-member">
                                <Avatar src={m.avatar_full_path} name={m.display_name || m.code} size={38} />
                                <span className="cw-person-body">
                                    <span className="cw-person-name">{m.display_name || m.code}{m.code === me ? ' · ' + T({ vi: 'Bạn', en: 'You', fr: 'Vous', es: 'Tú', id: 'Anda', ja: '自分', zh: '你', ko: '나', hi: 'आप', ar: 'أنت' }) : ''}</span>
                                </span>
                                {isGroup && m.code === leader && <span className="cw-leader">{T({ vi: 'Trưởng nhóm', en: 'Leader', fr: 'Chef', es: 'Líder', id: 'Ketua grup', ja: 'リーダー', zh: '群主', ko: '그룹장', hi: 'समूह प्रमुख', ar: 'قائد المجموعة' })}</span>}
                                {isLeader && m.code !== leader && <IconButton icon="close" size={15} className="cw-icon-btn--danger" label={{ vi: 'Mời khỏi nhóm', en: 'Remove member', fr: 'Retirer membre', es: 'Quitar miembro', id: 'Keluarkan', ja: 'メンバー削除', zh: '移出群组', ko: '멤버 내보내기', hi: 'सदस्य हटाएँ', ar: 'إزالة العضو' }} onClick={function () { remove(m); }} disabled={busy} />}
                            </div>
                        );
                    })}
                </div>
                {isGroup && (
                    <div className="cw-section cw-danger-zone">
                        <button type="button" className="cw-danger-btn" onClick={props.onLeave}><Icon name="logout" size={17} />{T({ vi: 'Rời nhóm', en: 'Leave group', fr: 'Quitter groupe', es: 'Dejar el grupo', id: 'Keluar grup', ja: 'グループ退出', zh: '退出群组', ko: '그룹 나가기', hi: 'समूह छोड़ें', ar: 'غادر المجموعة' })}</button>
                        {isLeader && <button type="button" className="cw-danger-btn" onClick={props.onDelete}><Icon name="trash" size={17} />{T({ vi: 'Xoá nhóm', en: 'Delete group', fr: 'Effacer groupe', es: 'Eliminar grupo', id: 'Hapus grup', ja: 'グループ削除', zh: '删除群组', ko: '그룹 삭제', hi: 'समूह हटाएँ', ar: 'حذف المجموعة' })}</button>}
                    </div>
                )}
            </div>
        </div>
    );
}

function AddMember(props) {
    var search = usePeopleSearch(props.me, props.exclude);
    return (
        <div className="cw-column">
            <SubHeader title={{ vi: 'Thêm thành viên', en: 'Add member', fr: 'Ajouter un membre', es: 'Añadir miembro', id: 'Tambah anggota', ja: 'メンバー追加', zh: '添加成员', ko: '멤버 추가', hi: 'सदस्य जोड़ें', ar: 'إضافة عضو' }} onBack={props.onBack} />
            <PeopleList search={search} busy={props.busy} action={{ vi: 'Thêm', en: 'Add', fr: 'Ajouter', es: 'Añadir', id: 'Tambah', ja: '追加', zh: '添加', ko: '추가', hi: 'जोड़ें', ar: 'إضافة' }} isPicked={function () { return false; }} onPick={props.onAdd} />
        </div>
    );
}

// ─── Photos, files, links ─────────────────────────────────────
var MEDIA_TABS = [
    { key: 'image', label: { vi: 'Ảnh', en: 'Photos', fr: 'Photos', es: 'Fotos', id: 'Foto', ja: '写真', zh: '图片', ko: '사진', hi: 'फ़ोटो', ar: 'الصور' }, empty: { vi: 'Chưa có ảnh nào được gửi', en: 'No photos shared yet', fr: 'Aucune photo partagée', es: 'Sin fotos compartidas', id: 'Belum ada foto dikirim', ja: '共有された写真はありません', zh: '暂无已发送的图片', ko: '공유된 사진이 없습니다', hi: 'कोई फ़ोटो साझा नहीं', ar: 'لا توجد صور مرسلة' } },
    { key: 'file', label: { vi: 'File', en: 'Files', fr: 'Fichiers', es: 'Archivos', id: 'Berkas', ja: 'ファイル', zh: '文件', ko: '파일', hi: 'फ़ाइलें', ar: 'الملفات' }, empty: { vi: 'Chưa có file nào được gửi', en: 'No files shared yet', fr: 'Aucun fichier partagé', es: 'Sin archivos compartidos', id: 'Belum ada berkas dikirim', ja: '共有されたファイルはありません', zh: '暂无已发送的文件', ko: '공유된 파일이 없습니다', hi: 'कोई फ़ाइल साझा नहीं', ar: 'لا توجد ملفات مرسلة' } },
    { key: 'link', label: { vi: 'Link', en: 'Links', fr: 'Liens', es: 'Enlaces', id: 'Tautan', ja: 'リンク', zh: '链接', ko: '링크', hi: 'लिंक', ar: 'الروابط' }, empty: { vi: 'Chưa có link nào được gửi', en: 'No links shared yet', fr: 'Aucun lien partagé', es: 'Sin enlaces compartidos', id: 'Belum ada tautan dikirim', ja: '共有されたリンクはありません', zh: '暂无已发送的链接', ko: '공유된 링크가 없습니다', hi: 'कोई लिंक साझा नहीं', ar: 'لا توجد روابط مرسلة' } }
];

function Media(props) {
    var chat = props.chat;
    var [tab, setTab] = useState('image');
    var [rows, setRows] = useState([]);
    var [state, setState] = useState('loading');
    var [more, setMore] = useState(false);
    var page = useRef(0), version = useRef(0);
    var load = useCallback(function (kind, next) {
        var current = ++version.current;
        setState('loading');
        call(msgAPI, 'media', { chat_id: chat.id, chat_code: chat.code, kind: kind, pageIndex: next || 0 }).then(function (data) {
            if (current !== version.current) return;
            var list = rowsOf(data);
            setRows(function (prev) { return next ? prev.concat(list) : list; });
            page.current = next || 0;
            setMore(!!(data && data.hasMore));
            setState('ready');
        }).catch(function () { if (current === version.current) setState('error'); });
    }, [chat]);
    useEffect(function () { setRows([]); load(tab, 0); }, [tab, load]);
    var current = MEDIA_TABS.find(function (t) { return t.key === tab; });
    var asMessage = function (r) { return Object.assign({ content_type: 'file' }, r); };
    return (
        <div className="cw-column">
            <SubHeader title={{ vi: 'Ảnh, file, link', en: 'Photos, files, links', fr: 'Photos, fichiers, liens', es: 'Fotos, archivos, links', id: 'Foto, berkas, tautan', ja: '写真・ファイル・リンク', zh: '图片、文件、链接', ko: '사진, 파일, 링크', hi: 'फ़ोटो, फ़ाइलें, लिंक', ar: 'صور، ملفات، روابط' }} onBack={props.onBack} />
            <div className="rs-segment" role="tablist">
                {MEDIA_TABS.map(function (t) {
                    return <button type="button" key={t.key} role="tab" aria-selected={tab === t.key} className={'rs-segment-btn' + (tab === t.key ? ' active' : '')} onClick={function () { setTab(t.key); }}>{T(t.label)}</button>;
                })}
            </div>
            <div className="cw-scroll cw-scroll--pad">
                {state === 'error' && <SidebarState type="error" text={{ vi: 'Không tải được dữ liệu', en: 'Could not load', fr: 'Échec du chargement', es: 'Error al cargar datos', id: 'Gagal memuat data', ja: 'データを読み込めません', zh: '无法加载数据', ko: '데이터 로드 실패', hi: 'डेटा लोड नहीं हो सका', ar: 'تعذر تحميل البيانات' }} onRetry={function () { load(tab, 0); }} />}
                {state === 'ready' && !rows.length && <SidebarState icon={tab === 'link' ? 'document' : 'empty'} text={current.empty} />}
                {tab === 'image' && rows.length > 0 && (
                    <div className="cw-media-grid">
                        {rows.map(function (r) {
                            var f = fileOf(r);
                            return <button type="button" key={r.id} className="cw-media-tile" title={f.name} onClick={function () { props.onOpenFile(asMessage(r), rows.map(asMessage)); }}><img src={f.url} alt={f.name} loading="lazy" /></button>;
                        })}
                    </div>
                )}
                {tab === 'file' && rows.map(function (r) {
                    var f = fileOf(r);
                    return (
                        <button type="button" key={r.id} className="cw-media-row" onClick={function () { props.onOpenFile(asMessage(r), rows.map(asMessage)); }}>
                            <span className="cw-file-ext">{fileExt(f.name)}</span>
                            <span className="cw-person-body"><span className="cw-person-name">{f.name}</span>
                                <span className="cw-person-meta">{[fileSize(f.size), r.user_name, sidebarDate(r.created_at, false)].filter(Boolean).join(' · ')}</span></span>
                        </button>
                    );
                })}
                {tab === 'link' && rows.map(function (r) {
                    return (
                        <a key={r.id} className="cw-media-row" href={r.url} target="_blank" rel="noopener noreferrer">
                            <span className="cw-link-icon"><Icon name="link" size={18} /></span>
                            <span className="cw-person-body"><span className="cw-person-name cw-link-text">{r.url}</span>
                                <span className="cw-person-meta">{[r.user_name, sidebarDate(r.created_at, false)].filter(Boolean).join(' · ')}</span></span>
                        </a>
                    );
                })}
                {state === 'loading' && <SidebarState type="loading" compact={rows.length > 0} />}
                {state === 'ready' && more && <button type="button" className="rs-more-btn" onClick={function () { load(tab, page.current + 1); }}>{T({ vi: 'Xem thêm', en: 'Load more', fr: 'Voir plus', es: 'Ver más', id: 'Lihat lagi', ja: 'もっと見る', zh: '查看更多', ko: '더 보기', hi: 'और देखें', ar: 'عرض المزيد' })}</button>}
            </div>
        </div>
    );
}
